"use client";

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { apiService } from '../services/api';
import { BarChart3, MessageCircle } from 'lucide-react';
import TelegramLoginWidget from '../components/TelegramLoginWidget';
import { TelegramUserData, AuthResponse } from '../config/api';

export default function LoginPage() {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [userData, setUserData] = useState<AuthResponse | null>(null);
  const [telegramUser, setTelegramUser] = useState<TelegramUserData | null>(null);

  const handleTelegramAuth = async (telegramData: TelegramUserData) => {
    setIsLoading(true);
    setMessage(null);
    setTelegramUser(telegramData);

    try {
      console.log('Telegram data received:', telegramData); // Добавляем отладку
      console.log('Telegram data keys:', Object.keys(telegramData)); // Показываем все ключи
      console.log('Telegram hash field:', telegramData.hash); // Проверяем наличие hash
      
      // Валидируем данные от Telegram
      if (!telegramData || !telegramData.id || !telegramData.first_name) {
        throw new Error('Неполные данные от Telegram');
      }
      
      if (!telegramData.hash) {
        throw new Error('Отсутствует поле hash для проверки подписи');
      }
      
      // Создаем data_check_string для проверки
      const dataCheckString = createDataCheckString(telegramData);
      console.log('Data check string:', dataCheckString); // Добавляем отладку
      
      // Проверяем, что dataCheckString не пустой
      if (!dataCheckString || dataCheckString.trim() === '') {
        throw new Error('Не удалось создать строку проверки данных');
      }
      
      // Отправляем данные на бэкенд
      console.log('Sending API request...'); // Добавляем отладку
      const response = await apiService.telegramWidgetLogin(dataCheckString);
      console.log('API response received:', response); // Добавляем отладку
      
      if (response && response.user_name) {
        setUserData(response);
        setMessage(`✅ Авторизация успешна! Добро пожаловать, ${response.first_name} ${response.second_name} (${response.user_name})`);
        console.log('Telegram auth response:', response);
        
        // Сохраняем данные пользователя в localStorage
        localStorage.setItem('user', JSON.stringify(response));
        
        // Редирект на дэшборд через 1.5 секунды
        // В статическом экспорте используем window.location вместо router.push
        setTimeout(() => {
          window.location.href = '/dashboard/';
        }, 1500);
      } else {
        setMessage('❌ Ошибка: Неверный формат ответа от сервера');
      }
    } catch (error) {
      setMessage(`❌ Ошибка авторизации: ${error instanceof Error ? error.message : 'Неизвестная ошибка'}`);
      console.error('Telegram login error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTelegramError = (error: string) => {
    setMessage(`❌ Ошибка Telegram: ${error}`);
  };

  // Функция для создания data_check_string согласно документации Telegram
  const createDataCheckString = (user: TelegramUserData): string => {
    const fields = [];
    
    // Добавляем ВСЕ поля включая hash (нужен для проверки подписи)
    if (user.id) fields.push(`id=${user.id}`);
    if (user.first_name) fields.push(`first_name=${user.first_name}`);
    if (user.last_name) fields.push(`last_name=${user.last_name}`);
    if (user.username) fields.push(`username=${user.username}`);
    if (user.photo_url) fields.push(`photo_url=${user.photo_url}`);
    if (user.auth_date) fields.push(`auth_date=${user.auth_date}`);
    if (user.hash) fields.push(`hash=${user.hash}`); // ВАЖНО: hash нужен для проверки подписи!
    
    // Сортируем поля в алфавитном порядке
    fields.sort();
    
    // Объединяем через \n
    return fields.join('\n');
  };

  const handleLogin = async () => {
    setIsLoading(true);
    setMessage(null);

    try {
      const response = await apiService.debugLogin({
        login: 'clappingseal',
        password: '12345'
      });

      // Если получили ответ с данными пользователя, значит авторизация успешна
      if (response && response.user_name) {
        setUserData(response);
        setMessage(`✅ Авторизация успешна! Добро пожаловать, ${response.first_name} ${response.second_name} (${response.user_name})`);
        console.log('Auth response:', response);
        
        // Сохраняем данные пользователя в localStorage
        localStorage.setItem('user', JSON.stringify(response));
        
        // Редирект на дэшборд через 1.5 секунды
        // В статическом экспорте используем window.location вместо router.push
        setTimeout(() => {
          window.location.href = '/dashboard/';
        }, 1500);
      } else {
        setMessage('❌ Ошибка: Неверный формат ответа от сервера');
      }
    } catch (error) {
      setMessage(`❌ Ошибка подключения: ${error instanceof Error ? error.message : 'Неизвестная ошибка'}`);
      console.error('Login error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center">
            <div className="bg-pink-100 dark:bg-pink-900/20 p-3 rounded-full">
              <BarChart3 className="h-12 w-12 text-pink-600 dark:text-pink-400" />
      </div>
          </div>
          <h2 className="mt-6 text-3xl font-bold text-gray-900 dark:text-white">
            безтендера
          </h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Админский дэшборд
          </p>
        </div>

        <div className="bg-white/80 dark:bg-zinc-900/80 backdrop-blur-sm rounded-xl border border-pink-200 dark:border-pink-800 p-8 shadow-lg">
          <div className="space-y-6">
            <div className="text-center">
              <MessageCircle className="h-8 w-8 text-pink-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Вход в систему
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Авторизуйтесь через Telegram
              </p>
            </div>

            <div className="space-y-6">
              {/* Telegram Login Widget */}
              <TelegramLoginWidget
                botName="beztendera_bot" // Замените на username вашего бота (без @)
                onAuth={handleTelegramAuth}
                onError={handleTelegramError}
              />

              {/* Разделитель */}

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white dark:bg-zinc-900 text-gray-500 dark:text-gray-400">или</span>
                </div>
              </div>

              {/* Тестовый вход для разработки - только в dev среде */}
              {process.env.NODE_ENV === 'development' && (
                <div className="space-y-4">
                  <div className="bg-pink-50 dark:bg-pink-900/20 p-4 rounded-lg">
                    <h4 className="text-sm font-medium text-pink-800 dark:text-pink-300 mb-2">
                      Тестовый вход (только для разработки):
                    </h4>
                    <div className="text-sm text-pink-700 dark:text-pink-400 space-y-1">
                      <div><strong>Логин:</strong> clappingseal</div>
                      <div><strong>Пароль:</strong> 12345</div>
                    </div>
                  </div>

                  <button
                    onClick={handleLogin}
                    disabled={isLoading}
                    className="w-full px-6 py-3 bg-gray-500 hover:bg-gray-600 disabled:bg-gray-300 text-white rounded-lg font-medium transition-colors shadow-sm hover:shadow-md flex items-center justify-center gap-2"
                  >
                    {isLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Авторизация...
                      </>
                    ) : (
                      'Тестовый вход'
                    )}
                  </button>
                </div>
                )}
            </div>

            {message && (
              <div className={`text-sm p-4 rounded-lg ${
                message.includes('✅') 
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' 
                  : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
              }`}>
                {message}
        </div>
            )}
            
            {telegramUser && process.env.NODE_ENV === 'development' && (
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <h4 className="font-medium text-blue-900 dark:text-blue-400 mb-2">Данные Telegram:</h4>
                <div className="text-sm text-blue-800 dark:text-blue-300 space-y-1">
                  <div><strong>ID:</strong> {telegramUser.id}</div>
                  <div><strong>Имя:</strong> {telegramUser.first_name}</div>
                  {telegramUser.last_name && <div><strong>Фамилия:</strong> {telegramUser.last_name}</div>}
                  {telegramUser.username && <div><strong>Username:</strong> @{telegramUser.username}</div>}
                  <div><strong>Дата авторизации:</strong> {new Date(telegramUser.auth_date * 1000).toLocaleString('ru-RU')}</div>
                </div>
              </div>
            )}
            
            {userData && process.env.NODE_ENV === 'development' && (
              <div className="bg-pink-50 dark:bg-pink-900/20 p-4 rounded-lg">
                <h4 className="font-medium text-pink-900 dark:text-pink-400 mb-2">Данные пользователя:</h4>
                <div className="text-sm text-pink-800 dark:text-pink-300 space-y-1">
                  <div><strong>ID:</strong> {userData.id}</div>
                  <div><strong>User ID:</strong> {userData.user_id}</div>
                  <div><strong>Имя:</strong> {userData.first_name}</div>
                  <div><strong>Фамилия:</strong> {userData.second_name}</div>
                  <div><strong>Username:</strong> {userData.user_name}</div>
                  <div><strong>Роль:</strong> {userData.role}</div>
                </div>
                <div className="mt-3 text-sm text-pink-700 dark:text-pink-400">
                  Переход на дэшборд...
                </div>
              </div>
            )}
            </div>
        </div>
      </div>
    </div>
  );
}
